/**
 * DamageDirection — Three combined cues for incoming damage:
 *   1. A red triangle on the screen edge pointing toward the attacker
 *      whenever one of the local player's drones takes damage.
 *   2. A brief full-screen-edge red flash on the same event.
 *   3. A persistent edge red pulse while the local swarm's aggregate HP
 *      is below 25%.
 *
 * The arrow's screen-space angle is derived from the attacker's world
 * position projected through the camera. We compute the vector from the
 * projected victim NDC to the projected attacker NDC and take its angle.
 * If either point is behind the camera we fall back to a world-space basis
 * dot product against the camera's right/forward.
 */
import * as THREE from 'three/webgpu';
import type { ClientNetManager } from '../networking/ClientNetManager';
import { ServerEvent } from '../../shared/types';
import { shouldIgnoreDamageEvent } from '../game/eventGating';
import type { Vec3 } from '../../shared/utils';

export interface DamageDirectionGateState {
  localSessionId: string;
  localDroneIds: ReadonlySet<string>;
  /** Aggregate fraction of local swarm HP, 0..1. Drives the low-HP pulse. */
  swarmHpFraction: number;
}

const ARROW_LIFETIME_MS = 1000;
const ARROW_CLEANUP_PAD_MS = 200;
const FLASH_MS = 360;
const MAX_ARROWS = 6;
const LOW_HP_THRESHOLD = 0.25;

export class DamageDirection {
  private layer: HTMLElement;
  private edgeFlash: HTMLElement;
  private lowHpRing: HTMLElement;
  private camera: THREE.Camera;
  private getGateState: () => DamageDirectionGateState;
  private attached = false;
  private arrows: HTMLElement[] = [];
  private projectVec = new THREE.Vector3();
  private flashTimer: ReturnType<typeof setTimeout> | null = null;
  private lowHpActive = false;

  constructor(
    layer: HTMLElement,
    camera: THREE.Camera,
    getGateState: () => DamageDirectionGateState,
  ) {
    this.layer = layer;
    this.camera = camera;
    this.getGateState = getGateState;

    this.edgeFlash = document.createElement('div');
    this.edgeFlash.className = 'damage-edge-flash';
    this.layer.appendChild(this.edgeFlash);

    this.lowHpRing = document.createElement('div');
    this.lowHpRing.className = 'damage-edge-low-hp';
    this.layer.appendChild(this.lowHpRing);
  }

  attach(net: ClientNetManager): void {
    if (this.attached) return;
    this.attached = true;
    net.onServerEvent((eventType, data) => {
      if (!this.attached) return;
      if (eventType !== ServerEvent.DamageDealt) return;
      if (shouldIgnoreDamageEvent(data)) return;
      const targetId = typeof data.targetDroneId === 'string' ? data.targetDroneId : '';
      if (!this.getGateState().localDroneIds.has(targetId)) return;
      const missed = (data as { missed?: unknown }).missed === true;
      // Miss = "an enemy shot at me but whiffed" — useful directional cue
      // (so the player still rotates toward the attacker), but no
      // full-screen edge flash since no damage actually landed.
      if (!missed) this.flashEdge();
      const attackerPos = data?.attackerPosition as Vec3 | undefined;
      const victimPos = data?.position as Vec3 | undefined;
      if (attackerPos && victimPos) {
        this.spawnArrow(victimPos, attackerPos, missed);
      }
    });
  }

  detach(): void {
    this.attached = false;
    if (this.flashTimer !== null) {
      clearTimeout(this.flashTimer);
      this.flashTimer = null;
    }
    for (const a of this.arrows) a.remove();
    this.arrows = [];
    this.edgeFlash.classList.remove('active');
    this.lowHpRing.classList.remove('active');
    this.lowHpActive = false;
  }

  /** Updates the persistent low-HP pulse based on the live gate state. */
  update(_deltaMs: number): void {
    const frac = this.getGateState().swarmHpFraction;
    const shouldPulse = frac > 0 && frac < LOW_HP_THRESHOLD;
    if (shouldPulse === this.lowHpActive) return;
    this.lowHpActive = shouldPulse;
    this.lowHpRing.classList.toggle('active', shouldPulse);
  }

  private flashEdge(): void {
    this.edgeFlash.classList.remove('active');
    void this.edgeFlash.offsetWidth;
    this.edgeFlash.classList.add('active');
    if (this.flashTimer !== null) clearTimeout(this.flashTimer);
    this.flashTimer = setTimeout(() => {
      this.edgeFlash.classList.remove('active');
      this.flashTimer = null;
    }, FLASH_MS);
  }

  private spawnArrow(victim: Vec3, attacker: Vec3, missed: boolean = false): void {
    const angle = this.computeScreenAngle(victim, attacker);
    if (this.arrows.length >= MAX_ARROWS) {
      this.arrows.shift()?.remove();
    }

    const arrow = document.createElement('div');
    // Miss variant — gray-tinted arrow so the player can tell the shot
    // whiffed without reading the floater. CSS owns the color swap via
    // `.damage-direction-arrow--miss`.
    arrow.className = missed
      ? 'damage-direction-arrow damage-direction-arrow--miss'
      : 'damage-direction-arrow';
    // Anchor at screen center, rotate by the screen-space bearing, then push
    // outward along the rotated X axis to land on the screen edge.
    arrow.style.transform = `translate(-50%, -50%) rotate(${angle}rad) translateX(38vmin)`;
    this.layer.appendChild(arrow);
    this.arrows.push(arrow);

    let removed = false;
    const cleanup = () => {
      if (removed) return;
      removed = true;
      arrow.removeEventListener('animationend', cleanup);
      const idx = this.arrows.indexOf(arrow);
      if (idx >= 0) this.arrows.splice(idx, 1);
      arrow.remove();
    };
    arrow.addEventListener('animationend', cleanup);
    setTimeout(cleanup, ARROW_LIFETIME_MS + ARROW_CLEANUP_PAD_MS);
  }

  /**
   * Returns the angle in radians from the victim's projected screen pos to
   * the attacker's. 0 = right, π/2 = down (CSS rotation conventions). Falls
   * back to a world-space basis projection when either point is behind the
   * camera.
   */
  private computeScreenAngle(victim: Vec3, attacker: Vec3): number {
    const cam = this.camera;

    this.projectVec.set(victim.x, victim.y, victim.z).project(cam);
    const vx = this.projectVec.x;
    const vy = this.projectVec.y;
    const vBehind = this.projectVec.z > 1;

    this.projectVec.set(attacker.x, attacker.y, attacker.z).project(cam);
    const ax = this.projectVec.x;
    const ay = this.projectVec.y;
    const aBehind = this.projectVec.z > 1;

    if (!vBehind && !aBehind) {
      const dx = ax - vx;
      // Flip Y because NDC up is +1 but CSS up is -y.
      const dy = -(ay - vy);
      return Math.atan2(dy, dx);
    }

    // Fallback: project (attacker - victim) onto camera right/forward.
    const right = new THREE.Vector3(1, 0, 0).applyQuaternion(cam.quaternion);
    const forward = new THREE.Vector3(0, 0, -1).applyQuaternion(cam.quaternion);
    const dx = attacker.x - victim.x;
    const dy = attacker.y - victim.y;
    const dz = attacker.z - victim.z;
    const dotR = dx * right.x + dy * right.y + dz * right.z;
    const dotF = dx * forward.x + dy * forward.y + dz * forward.z;
    return Math.atan2(-dotF, dotR);
  }
}
